import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  IconButton,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { getPlayoffBonusColumns } from '../../../components/standings/playoffScoring';
import { useCanManageLeague } from '../../../contexts/PermissionsContext';

const STANDINGS_COLUMNS = [
  { key: 'position', label: 'Pos', align: 'center' },
  { key: 'teamName', label: 'Team', align: 'left' },
  { key: 'matchesPlayed', label: 'P', align: 'center' },
  { key: 'wins', label: 'W', align: 'center' },
  { key: 'draws', label: 'D', align: 'center' },
  { key: 'losses', label: 'L', align: 'center' },
  { key: 'goalsFor', label: 'GF', align: 'center' },
  { key: 'goalsAgainst', label: 'GA', align: 'center' },
  { key: 'goalDifference', label: 'GD', align: 'center' },
  { key: 'goalBonusPoints', label: 'Goal Bonus', align: 'center' },
  { key: 'shutoutPoints', label: 'Shutout', align: 'center' },
  { key: 'totalPoints', label: 'Pts', align: 'center' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const PlayoffConfigPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const canManageLeague = useCanManageLeague(parseInt(leagueId));

  // Config state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isActive, setIsActive] = useState(false);

  // Pools state
  const [pools, setPools] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState({});
  const [poolSaving, setPoolSaving] = useState(null);

  // Standings state
  const [standings, setStandings] = useState(null);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const bonusColumns = getPlayoffBonusColumns(standings?.scoring);
  const standingsColumns = STANDINGS_COLUMNS.filter((col) =>
    (col.key !== 'goalBonusPoints' || bonusColumns.goalBonus) && (col.key !== 'shutoutPoints' || bonusColumns.shutout));
  const [recalculating, setRecalculating] = useState(false);

  // Bracket state
  const [bracket, setBracket] = useState(null);
  const [bracketLoading, setBracketLoading] = useState(false);
  const [leagueSlug, setLeagueSlug] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [config, teamsData] = await Promise.all([
        unifiedApi.getPlayoffConfig(leagueId),
        unifiedApi.getLeagueTeamsWithManagers(leagueId),
      ]);

      if (config) {
        setIsActive(config.isActive);
        setPools(config.pools || []);
      } else {
        setIsActive(false);
        setPools([]);
      }

      setAllTeams(teamsData || []);
    } catch (err) {
      setError('Failed to load playoff configuration.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const fetchLeagueSlug = useCallback(async () => {
    try {
      const league = await unifiedApi.getLeagueById(leagueId);
      if (league?.slug) {
        setLeagueSlug(league.slug);
      }
    } catch {
      // Non-critical — bracket/standings display may not work
    }
  }, [leagueId]);

  const fetchStandings = useCallback(async () => {
    if (!isActive) return;
    setStandingsLoading(true);
    try {
      const data = await unifiedApi.getPlayoffStandings(leagueId);
      setStandings(data);
    } catch {
      // Non-critical
    } finally {
      setStandingsLoading(false);
    }
  }, [leagueId, isActive]);

  const fetchBracket = useCallback(async () => {
    if (!isActive || !leagueSlug) return;
    setBracketLoading(true);
    try {
      const data = await unifiedApi.getPlayoffBracket(leagueSlug);
      setBracket(data);
    } catch {
      // Non-critical
    } finally {
      setBracketLoading(false);
    }
  }, [leagueSlug, isActive]);

  useEffect(() => {
    fetchConfig();
    fetchLeagueSlug();
  }, [fetchConfig, fetchLeagueSlug]);

  useEffect(() => {
    if (!loading) {
      fetchStandings();
    }
  }, [fetchStandings, loading]);

  useEffect(() => {
    if (!loading && leagueSlug) {
      fetchBracket();
    }
  }, [fetchBracket, loading, leagueSlug]);

  const handleSaveConfig = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await unifiedApi.upsertPlayoffConfig(leagueId, { isActive });
      setPools(result.pools || []);
      setSuccess('Playoff configuration saved.');
    } catch (err) {
      setError('Failed to save playoff configuration.');
    } finally {
      setSaving(false);
    }
  };

  // Pool management
  const assignedTeamIds = pools.flatMap(p => p.teams.map(t => t.teamId));
  const unassignedTeams = allTeams.filter(t => !assignedTeamIds.includes(t.id));

  const handleToggleTeamSelect = (poolId, teamId) => {
    setSelectedTeamIds(prev => {
      const current = prev[poolId] || [];
      const updated = current.includes(teamId)
        ? current.filter(id => id !== teamId)
        : [...current, teamId];
      return { ...prev, [poolId]: updated };
    });
  };

  const handleAssignTeams = async (poolId) => {
    const teamIds = selectedTeamIds[poolId] || [];
    if (teamIds.length === 0) return;

    setPoolSaving(poolId);
    setError(null);
    try {
      const pool = pools.find(p => p.id === poolId);
      const existingIds = pool ? pool.teams.map(t => t.teamId) : [];
      const allPoolTeamIds = [...existingIds, ...teamIds];

      await unifiedApi.setPlayoffPoolTeams(leagueId, poolId, allPoolTeamIds);
      setSelectedTeamIds(prev => ({ ...prev, [poolId]: [] }));

      // Refresh pools
      const updatedPools = await unifiedApi.getPlayoffPools(leagueId);
      setPools(updatedPools || []);
      setSuccess('Teams assigned to pool.');
    } catch (err) {
      setError('Failed to assign teams to pool.');
    } finally {
      setPoolSaving(null);
    }
  };

  const handleRemoveTeam = async (poolId, teamId) => {
    setPoolSaving(poolId);
    setError(null);
    try {
      await unifiedApi.removeTeamFromPool(leagueId, poolId, teamId);
      const updatedPools = await unifiedApi.getPlayoffPools(leagueId);
      setPools(updatedPools || []);
    } catch (err) {
      setError('Failed to remove team from pool.');
    } finally {
      setPoolSaving(null);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    setError(null);
    try {
      await unifiedApi.recalculatePlayoffStandings(leagueId);
      await fetchStandings();
      setSuccess('Standings recalculated.');
    } catch (err) {
      setError('Failed to recalculate standings.');
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Dashboard
        </Button>
        <Typography variant="h4" gutterBottom>
          <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Playoff Configuration
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Section 1: Playoff Configuration */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Playoff Settings
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={!canManageLeague}
                />
              }
              label="Playoffs Active"
            />
            {canManageLeague && (
            <Box>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveConfig}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Box>
            )}
          </Stack>
        </Paper>

        {/* Section 2: Pool Management */}
        {pools.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pool Management
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              {pools.map((pool) => (
                <Box key={pool.id} sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {pool.name}
                  </Typography>

                  {/* Assigned teams */}
                  {pool.teams.length > 0 ? (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {pool.teams.map((team) => (
                        <Box
                          key={team.teamId}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Typography variant="body2">{team.teamName}</Typography>
                          {canManageLeague && (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Remove ${team.teamName} from pool`}
                            onClick={() => handleRemoveTeam(pool.id, team.teamId)}
                            disabled={poolSaving === pool.id}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No teams assigned
                    </Typography>
                  )}

                  {/* Assign teams from unassigned list */}
                  {canManageLeague && unassignedTeams.length > 0 && (
                    <>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Add teams:
                      </Typography>
                      <Stack spacing={0.5} sx={{ maxHeight: 200, overflow: 'auto', my: 1 }}>
                        {unassignedTeams.map((team) => (
                          <FormControlLabel
                            key={team.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={(selectedTeamIds[pool.id] || []).includes(team.id)}
                                onChange={() => handleToggleTeamSelect(pool.id, team.id)}
                              />
                            }
                            label={<Typography variant="body2">{team.name}</Typography>}
                          />
                        ))}
                      </Stack>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleAssignTeams(pool.id)}
                        disabled={
                          poolSaving === pool.id ||
                          !(selectedTeamIds[pool.id]?.length > 0)
                        }
                      >
                        {poolSaving === pool.id ? 'Assigning...' : 'Assign Selected'}
                      </Button>
                    </>
                  )}
                </Box>
              ))}
            </Box>

            {/* Unassigned teams summary */}
            {unassignedTeams.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {unassignedTeams.length} team{unassignedTeams.length !== 1 ? 's' : ''} not assigned to a pool
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Section 3: Pool Standings Preview */}
        {isActive && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Pool Standings
              </Typography>
              {canManageLeague && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRecalculate}
                disabled={recalculating}
              >
                {recalculating ? 'Recalculating...' : 'Recalculate'}
              </Button>
              )}
            </Box>

            {standingsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : standings?.pools?.length > 0 ? (
              <Stack spacing={3}>
                {standings.pools.map((pool) => (
                  <Box key={pool.poolId}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {pool.poolName}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {standingsColumns.map((col) => (
                              <TableCell key={col.key} align={col.align} sx={{ fontWeight: 'bold' }}>
                                {col.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pool.standings.map((row) => (
                            <TableRow key={row.teamId}>
                              {standingsColumns.map((col) => (
                                <TableCell key={col.key} align={col.align}>
                                  {row[col.key]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No standings data available. Assign teams to pools and play some games first.
              </Typography>
            )}
          </Paper>
        )}

        {/* Section 4: Bracket Preview */}
        {isActive && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bracket
            </Typography>

            {bracketLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : bracket ? (
              <Stack spacing={2}>
                {bracket.semiFinals?.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Semi-Finals
                    </Typography>
                    {bracket.semiFinals.map((match, idx) => (
                      <BracketMatchCard key={match.gameId || idx} match={match} />
                    ))}
                  </>
                )}
                {bracket.final && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>
                      Final
                    </Typography>
                    <BracketMatchCard match={bracket.final} />
                  </>
                )}
                {!bracket.semiFinals?.length && !bracket.final && (
                  <Typography variant="body2" color="text.secondary">
                    No Semi-Final or Final games scheduled yet. Games with those types appear here automatically.
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No bracket data available.
              </Typography>
            )}
          </Paper>
        )}
      </Stack>
    </AdminLayout>
  );
};

const BracketMatchCard = ({ match }) => {
  const hasScore = match.homeScore !== null && match.awayScore !== null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body1" sx={{ fontWeight: match.winnerTeamId === match.homeTeamId ? 'bold' : 'normal' }}>
            {match.homeTeamName || 'TBD'}
          </Typography>
          {hasScore ? (
            <Chip
              label={`${match.homeScore} - ${match.awayScore}`}
              size="small"
              color="primary"
              variant="outlined"
              clickable={false}
              onClick={() => {}}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">vs</Typography>
          )}
          <Typography variant="body1" sx={{ fontWeight: match.winnerTeamId === match.awayTeamId ? 'bold' : 'normal' }}>
            {match.awayTeamName || 'TBD'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatDate(match.start)}
          </Typography>
          {match.gameId > 0 && (
            <Button
              size="small"
              component={Link}
              to={`/games/${match.gameId}/edit`}
            >
              Edit Game
            </Button>
          )}
        </Box>
      </Box>
      {match.winnerTeamName && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
          Winner: {match.winnerTeamName}
        </Typography>
      )}
    </Paper>
  );
};

export default PlayoffConfigPage;
